/**
 * tierSelect.js
 *
 * Resolves the H.265 decode tier ONCE per page load, then every card uses
 * it — PLAYBACK-HANDOFF.md §8: "Resolve the tier once per page load from
 * capability probes... Never silently fall back to H.264; there is no
 * H.264."
 *
 *   1. MSE via hls.js  — MediaSource.isTypeSupported('video/mp4; codecs="hvc1.1.6.L93.B0"')
 *   2. WebCodecs       — VideoDecoder.isConfigSupported() for 'hev1...' then 'hvc1...'
 *   3. h265web.js WASM — always available; the guaranteed-to-work fallback
 *
 * ⚠ Tier 2 has never been exercised on real hardware (handoff §8 notes:
 * "the sandbox host has no HEVC WebCodecs. Validate it once on a machine
 * with hardware HEVC before relying on it; it fails loudly.").
 */

let cached = null;

function checkMseHevc() {
  try {
    return !!(window.MediaSource && MediaSource.isTypeSupported('video/mp4; codecs="hvc1.1.6.L93.B0"'));
  } catch (_) {
    return false;
  }
}

function detectWebCodecsHevc() {
  if (typeof VideoDecoder === 'undefined') return Promise.resolve({ supported: false, codec: null });
  // Try 'hev1...' (in-band params, what this feature's demuxer produces)
  // then 'hvc1...' — some engines report support under one codec string but
  // not the other; both denote the same Main Profile L3.1 bitstream.
  const codecStrs = ['hev1.1.6.L93.B0', 'hvc1.1.6.L93.B0'];
  const tryOne = (i) => {
    if (i >= codecStrs.length) return Promise.resolve({ supported: false, codec: null });
    let p;
    try {
      p = VideoDecoder.isConfigSupported({ codec: codecStrs[i], codedWidth: 1280, codedHeight: 720 });
    } catch (_) {
      return tryOne(i + 1);
    }
    return Promise.resolve(p).then(
      (r) => (r && r.supported ? { supported: true, codec: codecStrs[i] } : tryOne(i + 1)),
      () => tryOne(i + 1),
    );
  };
  return tryOne(0);
}

/**
 * @returns {Promise<{tier: 'mse'|'webcodecs'|'wasm', webcodecsCodec: string|null}>}
 */
export function resolveDecodeTier() {
  if (!cached) {
    cached = (async () => {
      const mseOk = checkMseHevc();
      const wc = await detectWebCodecsHevc();
      const tier = mseOk ? 'mse' : (wc.supported ? 'webcodecs' : 'wasm');
      return { tier, webcodecsCodec: wc.codec };
    })();
  }
  return cached;
}
